import { useCallback, useEffect, useRef } from 'react'
import { useParams, useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector, useStore } from 'react-redux'
import { setSurveyFlow } from '../../../actions/surveyFlowAction'
import SurveyDialog from './SurveyDialog'
import SurveyBuilder from '../SurveyBuilder'
import { API_PATH } from '../../../Constants/path';
import { KEYS_OF_FILTER_KEY_LIST, KEYS_OF_IS_CREATE, KEYS_OF_TEMP_DRAFT_DATA, PREFIX_OF_TEMP_NODE } from '../constant';
import BottomBarFromEditCreateDisplay from '../../_v2/common/BottomBarFromEditCreateDisplay';
import SaveIcon from '@mui/icons-material/Save'
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LowPriorityIcon from '@mui/icons-material/LowPriority'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import PowerIcon from '@mui/icons-material/Power';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import MUICard from '../../common/card/MUICard';
import CardTitle from '../../common/card/CardTitle';
import { constants } from '../../../Constants/constants';
import { GlobalDialog } from '../../GlobalDialog';
import useUpload from '../hooks/useUpload';
import useFilterKey from '../hooks/useFilterKey';
import { useDialogs } from '../../../hooks/useDialog';
import { useSurveyData } from '../../../hooks/useSurveyData';
import { useSurveyDraft } from '../../../hooks/useSurveyDraft';
import { surveyService } from '../../../services/surveyService';
import { SURVEY_STATUS, SURVEY_ERROR_MESSAGES } from '../../../Constants/surveyConstants';
import { formatSurveyForSubmission } from '../../../utils/surveyUtils';


// survey detail page
export default function SurveyPanel() {

    const store = useStore()
    const savedSurveyFlow = useSelector(state => state.surveyFlow.ui.surveyFlow)
    const dispatch = useDispatch()

    const params = useParams()
    const location = useLocation()
    const history = useHistory()

    const canvasRef = useRef(null)

    const { convertExcelToJson } = useUpload()
    const { init } = useFilterKey(KEYS_OF_FILTER_KEY_LIST)

    // Use custom hooks for state management
    const { dialogs, openDialog, closeDialog, isDialogOpen } = useDialogs()
    // Only fetch survey data if it's a real ID (not a temp create mode ID)
    const shouldFetchData = params?.id && !params?.id?.includes(PREFIX_OF_TEMP_NODE)
    const { data, loading, error, refetch } = useSurveyData(shouldFetchData ? params?.id : null)
    const { getDraftData, clearDraft, saveDraftToTemp } = useSurveyDraft()

    const uploadIptRef = useRef(null)
    const savedSurveyFlowRef = useRef(savedSurveyFlow)
    savedSurveyFlowRef.current = savedSurveyFlow

    // Sync fetched survey data into Redux so savedSurveyFlow is populated.
    // Deep-clone to break shared references — ReactFlow nodes hold references
    // to the same question objects, and mutating them would mutate Redux state.
    useEffect(() => {
        if (data?.id) {
            dispatch(setSurveyFlow(JSON.parse(JSON.stringify(data))))
        }
    }, [data, dispatch])

    const handleSave = async () => {
        const nodes = canvasRef.current?.getNodes() || []
        const edges = canvasRef.current?.getEdges() || []
        const payload = formatSurveyForSubmission(savedSurveyFlow, nodes, edges)

        try {
            let result
            if (savedSurveyFlow?.id) {
                result = await surveyService.update(savedSurveyFlow.id, payload, store)
            } else {
                result = await surveyService.create(payload, store)
                history.replace(`/${constants.PATH.SURVEY.BASE}/` + result?.id)
            }
            updateSubmitDataToStore(result)
        } catch (err) {
            console.error(SURVEY_ERROR_MESSAGES.SAVE_FAILED, err)
        }
    }

    const handleToggleStatus = async () => {
        const current = savedSurveyFlowRef.current
        const id = current?.id
        const currentStatus = current?.status
        if (!id || !currentStatus) return

        const newStatus = currentStatus === SURVEY_STATUS.ACTIVE ? SURVEY_STATUS.PAUSED : SURVEY_STATUS.ACTIVE

        // Optimistic update — toggle immediately for responsive UI
        dispatch(setSurveyFlow({ ...current, status: newStatus }))

        try {
            // baseFetch.put() unwraps the response: successCallback receives
            // data.payload, so a successful call resolves with "SUCCESS".
            // If it throws or rejects, the API failed — revert.
            await surveyService.toggleStatus(id, newStatus, store)
        } catch (err) {
            // Revert on failure
            dispatch(setSurveyFlow(current))
            console.error(SURVEY_ERROR_MESSAGES.STATUS_CHANGE_FAILED, err)
        }
    }

    const handleDelete = async () => {
        try {
            await surveyService.delete(savedSurveyFlowRef.current?.id, store)
            history.replace({
                pathname: `/${constants.PATH.SURVEY.BASE}`,
                search: location.search,
            })
        } catch (err) {
            console.error(SURVEY_ERROR_MESSAGES.DELETE_FAILED, err)
        }
    }

    const showDeleteConfirmDialog = () => {
        openDialog('delete', {
            title: 'Confirm Delete',
            message: 'Are you sure you want to delete this survey?'
        })
    }

    const updateSubmitDataToStore = useCallback((surveyData) => {
        // Merge form data into existing state to preserve id, status, questionList
        dispatch(setSurveyFlow({ ...savedSurveyFlowRef.current, ...surveyData }))
        closeDialog('edit')
    }, [dispatch, closeDialog])

    const handleRefresh = useCallback(async () => {
        const fresh = await refetch?.()
        if (fresh?.id) {
            dispatch(setSurveyFlow(JSON.parse(JSON.stringify(fresh))))
        }
    }, [refetch, dispatch])

    const bottomBtns = [
        {
            label: { key: 'save' },
            onClick: handleSave,
            className: 'margin-right-16',
            icon: <SaveIcon />,
            disabled: false,
        },
        {
            label: { key: 'edit' },
            onClick: () => openDialog('edit'),
            className: 'margin-right-16',
            icon: <EditIcon />,
            disabled: false,
        },
        {
            label: { key: 'clear' },
            onClick: handleRefresh,
            className: 'margin-right-16',
            icon: <RefreshIcon />,
            disabled: false,
        },
        {
            variant: constants.STYLE.VARIANT.CONTAINED,
            color: (savedSurveyFlow.status === SURVEY_STATUS.ACTIVE) ? constants.STYLE.ERROR : constants.STYLE.SUCCESS,
            icon: (savedSurveyFlow.status === SURVEY_STATUS.ACTIVE) ? <PowerOffIcon /> : <PowerIcon />,
            label: { key: (savedSurveyFlow.status === SURVEY_STATUS.ACTIVE) ? 'disabled' : 'enabled' },
            className: 'special-button-margin-left margin-right-16',
            onClick: handleToggleStatus,
        },
        new URLSearchParams(location?.search).get(KEYS_OF_IS_CREATE) ? null : {
            color: constants.STYLE.ERROR,
            label: { key: 'deleteBtn' },
            onClick: showDeleteConfirmDialog,
            className: 'margin-right-16',
            icon: <DeleteIcon />,
            disabled: false,
        }
    ].filter(i => i)

    useEffect(() => {
        if (!params?.id) return

        // Skip getOne for temp nodes (create mode)
        if (params?.id?.includes(PREFIX_OF_TEMP_NODE)) return

        // Initialize upload listener and filter keys
        const fileInput = document.getElementById('uploadFile');
        if (fileInput) {
            const handleFileChange = async (event) => {
                const file = event.target.files[0];
                const result = await convertExcelToJson(file);
                if (result) {
                    const currentFlow = savedSurveyFlowRef.current
                    const newData = {
                        ...currentFlow,
                        ...result?.common,
                        questionList: [...(currentFlow?.questionList || []), ...result?.detail]
                    }
                    dispatch(setSurveyFlow(newData))
                    uploadIptRef.current.value = null
                }
            }
            fileInput.addEventListener('change', handleFileChange)

            // Cleanup: remove the listener when the effect re-runs or component unmounts
            return () => {
                fileInput.removeEventListener('change', handleFileChange)
            }
        }

        // Extract filter keys from loaded data
        if (data?.questionList?.length > 0) {
            const existingFilterKey = data?.questionList
                ?.map(i => ({ value: i?.filterKey, ts: Number.MAX_SAFE_INTEGER }))
                .filter(i => i.value)
            init(existingFilterKey)
        }
    }, [params?.id, data?.questionList, convertExcelToJson, dispatch, init])

    return (
        <MUICard
            header={
                <CardTitle>
                    Edit Survey: ({params?.id}) {savedSurveyFlow?.title?.EN}
                </CardTitle>
            }
            actions={<BottomBarFromEditCreateDisplay bottomBtns={bottomBtns} />}
            sx={{ gap: 0 }}
        >
            {/* edit dialog */}
            <SurveyDialog
                isEdit={true}
                open={isDialogOpen('edit')}
                toggleOpen={(open) => open ? openDialog('edit') : closeDialog('edit')}
                handleSubmit={updateSubmitDataToStore}
            />

            {/* main flow canvas — uses new pluggable SurveyBuilder */}
            <SurveyBuilder
                canvasRef={canvasRef}
                initList={data?.questionList}
            />

            {/* Global dialog for delete */}
            {isDialogOpen('delete') && (
                <GlobalDialog
                    dialogInfo={{
                        open: true,
                        onClose: () => closeDialog('delete'),
                        dialogContentText: { key: 'deleteContent' },
                        dialogActions: [
                            { label: { key: 'close' }, onClick: () => closeDialog('delete'), icon: <CloseIcon /> },
                            { label: { key: 'confirm' }, onClick: () => {
                                handleDelete()
                                closeDialog('delete')
                            }, icon: <DoneIcon /> },
                        ],
                    }}
                />
            )}

            {/* for upload */}
            <input ref={uploadIptRef} hidden type="file" name="uploadFile" id="uploadFile" />
        </MUICard>
    )
}
